<template>
  <div>
    <modal
      :name="modalName"
      width="98%"
      height="auto"
      :max-width="1920"
      :adaptive="true"
      :scrollable="true"
      @opened="focusClose"
      @before-close="beforeClose"
    >
    <div
      ref="dialog"
      class="automation-delivery"
      role="dialog"
      aria-modal="true"
      aria-labelledby="automation-delivery-title"
      @keydown.tab="trapFocus"
      @keyup.esc.stop.prevent="$emit('close')"
    >
      <AppModalHeader
        ref="header"
        design="new"
        :title="`Delivery #${deliveryId}`"
        :eyebrow="`Follow-up Automation · Run #${runId}`"
        title-id="automation-delivery-title"
        @close="$emit('close')"
      />
      <div class="automation-delivery-body automation-stack">
        <ResourceState :resource="detail" label="delivery" @reload="loadDetail">
          <template #default="{ data: delivery }">
            <div class="automation-delivery-layout">
              <section
                v-if="delivery.attention"
                class="automation-delivery-skipped"
                :class="{ 'automation-delivery-skipped--failed': delivery.attention.level === 'send_failed' }"
              >
                <div class="automation-delivery-skipped__heading">
                  <AppStatusBadge
                    design="new"
                    compact
                    :tone="delivery.attention.level === 'send_failed' ? 'red' : 'amber'"
                    :label="delivery.attention.level === 'send_failed' ? 'Send failed' : 'Not sent'"
                  />
                  <strong>{{ delivery.attention.title }}</strong>
                </div>
                <p>{{ delivery.attention.message }}</p>
                <p><strong>Next action:</strong> {{ delivery.attention.recommended_action }}</p>
                <small>
                  {{ delivery.attention.code }} · Provider request:
                  {{ delivery.attention.provider_attempted ? 'Yes' : 'No' }} · Automatic retry:
                  {{ delivery.attention.automatic_retry_scheduled ? 'Scheduled' : 'Not scheduled' }}
                </small>
              </section>
              <section v-else-if="isSkipped(delivery)" class="automation-delivery-skipped">
                <div class="automation-delivery-skipped__heading">
                  <AppStatusBadge design="new" compact tone="amber" label="Skipped" />
                  <strong>Delivery was not attempted</strong>
                </div>
                <p>{{ skipReason(delivery) }}</p>
              </section>

              <div class="automation-detail-grid automation-delivery-summary">
                <DetailCard title="Delivery">
                  <template #actions>
                    <AppStatusBadge
                      design="new"
                      compact
                      :tone="statusTone(delivery.status)"
                      :label="display(delivery.status)"
                    />
                    <AppStatusBadge
                      v-if="delivery.has_attention"
                      design="new"
                      compact
                      tone="amber"
                      label="Needs attention"
                    />
                  </template>
                  <Fields :fields="deliveryFields(delivery)" />
                  <p v-if="delivery.error && !delivery.attention && !isSkipped(delivery)" class="automation-problem automation-delivery-note">
                    <strong>{{ delivery.error.code }}</strong> · {{ delivery.error.message }}
                  </p>
                </DetailCard>

                <DetailCard v-if="hasMessage(delivery)" title="Message">
                  <template #actions>
                    <AppStatusBadge
                      design="new"
                      compact
                      tone="neutral"
                      :label="basisLabel(delivery.content.basis)"
                      :title="contentBasisHint(delivery.content.basis)"
                    />
                  </template>
                  <strong v-if="delivery.content.subject" class="automation-delivery-subject">
                    {{ delivery.content.subject }}
                  </strong>
                  <pre v-if="delivery.content.text" class="automation-delivery-message">{{ delivery.content.text }}</pre>
                  <p v-else-if="delivery.content.unavailable_reason" class="automation-muted">
                    {{ delivery.content.unavailable_reason }}
                  </p>
                  <p v-else class="automation-muted">No message text was recorded.</p>
                  <p
                    v-if="delivery.content.basis === 'current_template_preview'"
                    class="automation-muted automation-delivery-note"
                  >
                    Current template preview. This is not proof of the message sent in the past.
                  </p>
                  <Payload
                    v-if="delivery.content.html !== null"
                    label="HTML source"
                    :payload="{ availability: 'stored', format: 'text', data: delivery.content.html }"
                  />
                  <Payload
                    v-if="delivery.content.template_source !== null"
                    label="Template source"
                    :payload="{
                      availability: 'stored',
                      format: 'text',
                      data: delivery.content.template_source
                    }"
                  />
                </DetailCard>

                <DetailCard
                  v-if="hasTemplateVariables(delivery)"
                  title="Variables"
                  class="automation-variables-card"
                >
                  <Fields
                    :fields="templateVariableFields(delivery)"
                    clamp-values
                    @view-value="openVariable"
                  />
                </DetailCard>

                <DetailCard v-if="hasProviderStatus(delivery)" title="Provider status">
                  <template v-if="delivery.communication" #actions>
                    <AppButton
                      design="new"
                      theme="dark"
                      size="small"
                      @click.native="$emit('open-communication', delivery.communication.id)"
                    >Communication details</AppButton>
                  </template>
                  <Fields :fields="providerFields(delivery)" />
                </DetailCard>

                <DetailCard title="Timeline">
                  <Fields :fields="timelineFields(delivery)" />
                </DetailCard>

                <DetailCard title="Template">
                  <Fields :fields="templateFields(delivery).concat(identifierFields(delivery))" />
                  <div v-if="delivery.client_request_id" class="automation-reference-row">
                    <span>{{ requestIdLabel(delivery) }}</span>
                    <span class="automation-reference-value">
                      <span :title="delivery.client_request_id">
                        {{ compactIdentifier(delivery.client_request_id) }}
                      </span>
                      <button
                        type="button"
                        class="automation-copy-button"
                        :title="`Copy full ${requestIdLabel(delivery).toLowerCase()}`"
                        :aria-label="`Copy full ${requestIdLabel(delivery).toLowerCase()}`"
                        @click="copyRequestId(delivery.client_request_id)"
                      >
                        <img src="@/assets/img/copy-account.svg" alt="" />
                      </button>
                    </span>
                  </div>
                </DetailCard>
              </div>

              <AppCard
                v-if="hasRecordedAttempts(delivery)"
                design="new"
                title="Recorded attempts"
                :subtitle="attemptExplanation(delivery)"
              >
                <template #title-help>
                  <InfoTooltip :content="help.recordedAttempts" aria-label="About recorded attempts" />
                </template>
                <PagedRecords :fetch="fetchAttempts" label="attempt records">
                  <template #default="{ rows }">
                    <div class="automation-table-scroll">
                      <table class="automation-table">
                        <thead>
                          <tr>
                            <th>Record</th>
                            <th>Kind</th>
                            <th>Sequence</th>
                            <th>Status</th>
                            <th>Started / recorded</th>
                            <th>Finished</th>
                            <th>HTTP status</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="row in rows">
                            <tr
                              :key="row.key"
                              data-interactive
                              class="automation-attempt-row"
                              tabindex="0"
                              :aria-expanded="String(attemptKey === row.key)"
                              @click="toggleAttempt(row.key)"
                              @keydown.enter.prevent="toggleAttempt(row.key)"
                              @keydown.space.prevent="toggleAttempt(row.key)"
                            >
                              <td>
                                <span
                                  class="automation-attempt-chevron"
                                  :class="{ 'automation-attempt-chevron--open': attemptKey === row.key }"
                                  aria-hidden="true"
                                >
                                  <img src="@/assets/img/accordion-arrow.svg" alt="" />
                                </span>
                                <span class="automation-link">{{ row.key }}</span>
                              </td>
                              <td>{{ row.kind }}</td>
                              <td>{{ display(row.sequence) }}</td>
                              <td>
                                <AppStatusBadge
                                  design="new"
                                  compact
                                  :tone="statusTone(row.status)"
                                  :label="display(row.status)"
                                />
                              </td>
                              <td>{{ dateTime(row.started_at) }}</td>
                              <td>{{ dateTime(row.finished_at) }}</td>
                              <td>{{ display(row.http_status) }}</td>
                              <td>{{ row.error ? row.error.message : '—' }}</td>
                            </tr>
                            <tr v-if="attemptKey === row.key" :key="`${row.key}:detail`">
                              <td colspan="8" class="automation-attempt-detail-cell">
                                <ResourceState :resource="attempt" label="attempt" @reload="loadAttempt">
                                  <template #default="{ data }">
                                    <div class="automation-attempt-detail">
                                      <DetailCard title="Info">
                                        <template #title-help>
                                          <InfoTooltip :content="help.attemptInfo" aria-label="About attempt information" />
                                        </template>
                                        <Fields :fields="attemptFields(data)" />
                                        <p class="automation-muted automation-delivery-note">
                                          Saved API/business payload. Raw HTTP headers and bytes may not have been recorded.
                                        </p>
                                      </DetailCard>
                                      <DetailCard title="Request">
                                        <template #title-help>
                                          <InfoTooltip :content="help.request" aria-label="About the provider request" />
                                        </template>
                                        <p v-if="attemptPayloadText(data.request) === null" class="automation-muted">
                                          {{ attemptPayloadUnavailable(data.request) }}
                                        </p>
                                        <pre v-else class="automation-attempt-json">{{ attemptPayloadText(data.request) }}</pre>
                                      </DetailCard>
                                      <DetailCard title="Response">
                                        <template #title-help>
                                          <InfoTooltip :content="help.response" aria-label="About the provider response" />
                                        </template>
                                        <p v-if="attemptPayloadText(data.response) === null" class="automation-muted">
                                          {{ attemptPayloadUnavailable(data.response) }}
                                        </p>
                                        <pre v-else class="automation-attempt-json">{{ attemptPayloadText(data.response) }}</pre>
                                      </DetailCard>
                                    </div>
                                  </template>
                                </ResourceState>
                              </td>
                            </tr>
                          </template>
                        </tbody>
                      </table>
                    </div>
                  </template>
                </PagedRecords>
              </AppCard>
              <AppCard v-show="eventCount !== 0" design="new" title="Provider events">
                <template #title-help>
                  <InfoTooltip :content="help.providerEvents" aria-label="About provider events" />
                </template>
                <PagedRecords
                  :fetch="fetchEvents"
                  label="provider events"
                  @count="eventCount = $event"
                  :empty-text="
                    delivery.provider.code === 'trengo'
                      ? 'Trengo has no complete event journal here. Recorded provider timestamps are shown in Delivery context.'
                      : 'No saved provider events are available.'
                  "
                >
                  <template #default="{ rows }">
                    <div class="automation-table-scroll">
                      <table class="automation-table">
                        <thead>
                          <tr>
                            <th>Event</th>
                            <th>Provider</th>
                            <th>Occurred</th>
                            <th>Recorded</th>
                            <th>Processing</th>
                            <th>Source</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="event in rows">
                            <tr
                              :key="event.key"
                              data-interactive
                              class="automation-attempt-row"
                              tabindex="0"
                              :aria-expanded="String(eventKey === event.key)"
                              @click="toggleEvent(event.key)"
                              @keydown.enter.prevent="toggleEvent(event.key)"
                              @keydown.space.prevent="toggleEvent(event.key)"
                            >
                              <td>
                                <span
                                  class="automation-attempt-chevron"
                                  :class="{ 'automation-attempt-chevron--open': eventKey === event.key }"
                                  aria-hidden="true"
                                >
                                  <img src="@/assets/img/accordion-arrow.svg" alt="" />
                                </span>
                                <strong>{{ display(event.type) }}</strong>
                              </td>
                              <td>{{ display(event.provider) }}</td>
                              <td>{{ dateTime(event.occurred_at) }}</td>
                              <td>{{ dateTime(event.recorded_at) }}</td>
                              <td>
                                <AppStatusBadge
                                  v-if="event.processing_status"
                                  design="new"
                                  compact
                                  :tone="statusTone(event.processing_status)"
                                  :label="display(event.processing_status)"
                                />
                                <template v-else>—</template>
                              </td>
                              <td>{{ event.source_model }} #{{ event.source_id }}</td>
                              <td>{{ event.error ? `${event.error.code} · ${event.error.message}` : '—' }}</td>
                            </tr>
                            <tr v-if="eventKey === event.key" :key="`${event.key}:detail`">
                              <td colspan="7" class="automation-attempt-detail-cell">
                                <p v-if="attemptPayloadText(event.payload) === null" class="automation-muted">
                                  {{ attemptPayloadUnavailable(event.payload) }}
                                </p>
                                <pre v-else class="automation-attempt-json">{{ attemptPayloadText(event.payload) }}</pre>
                              </td>
                            </tr>
                          </template>
                        </tbody>
                      </table>
                    </div>
                  </template>
                </PagedRecords>
              </AppCard>
            </div>
          </template>
        </ResourceState>
      </div>
    </div>
    </modal>
    <VariableValueModal
      v-if="selectedVariable"
      :key="`${deliveryId}:${selectedVariable.label}`"
      :delivery-id="deliveryId"
      :label="selectedVariable.label"
      :value="selectedVariable.value"
      @close="selectedVariable = null"
    />
  </div>
</template>
<script>
import {
  getFollowUpAutomationDelivery,
  getFollowUpAutomationAttempts,
  getFollowUpAutomationAttempt,
  getFollowUpAutomationEvents
} from '@/http'
import resources, { resource } from './resources'
import { display, dateTime, statusTone, payloadText } from './model'
import ResourceState from './ResourceState.vue'
import Fields from './Fields.vue'
import Payload from './Payload.vue'
import PagedRecords from './PagedRecords.vue'
import DetailCard from './DetailCard.vue'
import InfoTooltip from './InfoTooltip.vue'
import VariableValueModal from './VariableValueModal.vue'

const HELP = Object.freeze({
  recordedAttempts: 'Saved attempts to send this delivery. Each row is one recorded provider request or provider record and shows whether it started, finished or failed. Internal HTTP retries may not be saved as separate rows.',
  attemptInfo: 'Technical summary of this attempt: the saved record type, its status, timing, HTTP result, provider message ID and any recorded error.',
  request: 'Data our system prepared for the provider. This can be a saved business payload rather than the exact raw HTTP request with headers and bytes.',
  response: 'Data returned by the provider or the result saved by our system. For a simulated attempt, it explains why no live request was sent.',
  providerEvents: 'Status updates recorded after or alongside the sending attempt, such as accepted, delivered, opened, clicked or failed. The available events and their payloads depend on the provider.'
})

export default {
  name: 'FollowUpAutomationDeliveryModal',
  components: {
    ResourceState,
    Fields,
    Payload,
    PagedRecords,
    DetailCard,
    InfoTooltip,
    VariableValueModal
  },
  mixins: [resources],
  props: { runId: { type: Number, required: true }, deliveryId: { type: Number, required: true } },
  data: () => ({
    detail: resource(),
    attempt: resource(),
    attemptKey: null,
    eventKey: null,
    eventCount: null,
    help: HELP,
    selectedVariable: null,
    previousOverflow: ''
  }),
  computed: {
    modalName() {
      return `automation-delivery-${this.runId}-${this.deliveryId}`
    }
  },
  mounted() {
    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    this.$modal.show(this.modalName)
    this.loadDetail()
  },
  beforeDestroy() {
    document.body.style.overflow = this.previousOverflow
  },
  methods: {
    display,
    dateTime,
    statusTone,
    payloadText,
    focusClose() {
      this.$refs.header.focusClose()
    },
    beforeClose(event) {
      event.cancel()
      this.$emit('close')
    },
    trapFocus(event) {
      const controls = Array.from(
        this.$refs.dialog.querySelectorAll(
          'button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled), [tabindex="0"]'
        )
      ).filter((node) => node.getClientRects().length)
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    async loadDetail() {
      this.attempt.request += 1
      this.attemptKey = null
      this.eventKey = null
      this.eventCount = null
      await this.loadResource(this.detail, () => getFollowUpAutomationDelivery(this.runId, this.deliveryId)
      )
    },
    toggleAttempt(key) {
      if (this.attemptKey === key) {
        this.attempt.request += 1
        this.attemptKey = null
        return
      }
      this.attemptKey = key
      this.loadAttempt()
    },
    loadAttempt() {
      return this.loadResource(this.attempt, () => getFollowUpAutomationAttempt(this.runId, this.deliveryId, this.attemptKey)
      )
    },
    fetchAttempts(params) {
      return getFollowUpAutomationAttempts(this.runId, this.deliveryId, params)
    },
    fetchEvents(params) {
      return getFollowUpAutomationEvents(this.runId, this.deliveryId, params)
    },
    toggleEvent(key) {
      this.eventKey = this.eventKey === key ? null : key
    },
    basisLabel(basis) {
      return (
        {
          saved: 'Saved record',
          current_lead: 'Current lead contact',
          unavailable: 'Unavailable',
          saved_message: 'Saved message',
          current_template_preview: 'Current template preview',
          redacted: 'Redacted'
        }[basis] || 'Unavailable'
      )
    },
    attemptExplanation(delivery) {
      if (delivery.recorded_attempts.kind === 'provider_record') return 'One saved provider record. Internal HTTP retries are not recorded as separate attempts.'
      if (delivery.recorded_attempts.kind === 'log') return 'Saved SendPulse log. Recorded time is not the HTTP start time; retry count is unavailable.'
      if (delivery.recorded_attempts.kind === 'none') return 'No provider record has been saved.'
      return `${display(delivery.recorded_attempts.count)} saved attempts · Latest ${dateTime(
        delivery.recorded_attempts.last_at
      )}`
    },
    attemptFields(data) {
      return this.compactFields([
        ['Kind', data.kind],
        ['Sequence', data.sequence],
        ['Status', data.status],
        ['Started / recorded', data.started_at && dateTime(data.started_at)],
        ['Finished', data.finished_at && dateTime(data.finished_at)],
        ['HTTP status', data.http_status],
        ['Provider message ID', data.provider_message_id],
        ['Error code', data.error && data.error.code],
        ['Error', data.error && data.error.message]
      ])
    },
    attemptPayloadText(payload) {
      return payloadText(payload)
    },
    attemptPayloadUnavailable(payload) {
      if (payload && payload.reason) return payload.reason
      if (payload && payload.availability === 'redacted') return 'The payload was redacted.'
      return 'The payload was not recorded.'
    },
    compactFields(fields) {
      return fields.filter(([, value]) => value !== null && value !== undefined && value !== '')
    },
    isSkipped(delivery) {
      return delivery.status && delivery.status.code === 'skipped'
    },
    skipReason(delivery) {
      return (delivery.error && delivery.error.message) || 'This delivery was skipped before any provider request was sent.'
    },
    hasMessage(delivery) {
      const content = delivery.content || {}
      return [content.subject, content.text, content.html, content.template_source]
        .some(value => value !== null && value !== undefined && value !== '')
    },
    hasProviderStatus(delivery) {
      return Boolean(delivery.communication || this.providerFields(delivery).length)
    },
    hasRecordedAttempts(delivery) {
      return Boolean(delivery.recorded_attempts && delivery.recorded_attempts.kind !== 'none')
    },
    deliveryFields(delivery) {
      const recipient = delivery.recipient
      const sender = delivery.sender
      return this.compactFields([
        ['Provider', delivery.provider],
        ['Channel', delivery.channel],
        ['Account', delivery.account],
        [
          'Recipient',
          recipient && recipient.value,
          recipient && this.recipientBasisHint(recipient.basis)
        ],
        ['Sender', sender && sender.value, sender && this.recipientBasisHint(sender.basis)]
      ])
    },
    timelineFields(delivery) {
      return this.compactFields([
        ['Scheduled', dateTime(delivery.due_at)],
        ['Started', delivery.started_at && dateTime(delivery.started_at)],
        ['Sent', delivery.sent_at && dateTime(delivery.sent_at)],
        ['Cancelled', delivery.cancelled_at && dateTime(delivery.cancelled_at)]
      ])
    },
    recipientBasisHint(basis) {
      if (basis === 'saved') return 'This value was saved with the delivery and reflects the recipient or sender at send time.'
      if (basis === 'current_lead') return 'A historical recipient was not saved. This is the lead’s current contact value.'
      return 'The historical value is unavailable.'
    },
    contentBasisHint(basis) {
      if (basis === 'saved_message') return 'The message content was saved with this delivery.'
      if (basis === 'current_template_preview') return 'This is generated from the current template and may differ from the historical message.'
      if (basis === 'redacted') return 'The stored message content was removed because it was marked as sensitive.'
      return 'The message content was not stored.'
    },
    providerFields(delivery) {
      return this.compactFields([
        ['Provider status', delivery.provider_status],
        ['Communication status', delivery.communication_status],
        ['Provider request', delivery.attention && (delivery.attention.provider_attempted ? 'Yes' : 'No')],
        ['Automatic retry', delivery.attention && (delivery.attention.automatic_retry_scheduled ? 'Scheduled' : 'Not scheduled')],
        ['Latest attempt', delivery.recorded_attempts.last_at && dateTime(delivery.recorded_attempts.last_at)],
        ...Object.entries(delivery.provider_timestamps || {}).map(([key, value]) => [
          key.replace(/_/g, ' ').replace(/^./, letter => letter.toUpperCase()),
          value && dateTime(value)
        ])
      ])
    },
    templateFields(delivery) {
      return this.compactFields([
        ['Title', delivery.template.title],
        ['Template code', delivery.template.code],
        ['Provider version', delivery.template.provider_version],
        ['Source synced', delivery.template.source_synced_at && dateTime(delivery.template.source_synced_at)]
      ])
    },
    identifierFields(delivery) {
      const labels = {
        message_id: 'Provider message ID',
        response_id: 'Provider response ID',
        ticket_id: 'Provider ticket ID',
        rfc_message_id: 'RFC message ID'
      }
      const providerIdentifiers = Object.entries(delivery.provider_identifiers || {}).map(([key, value]) => [
        labels[key] || key.replace(/_/g, ' '),
        value
      ])
      const conversation = delivery.conversation
      return this.compactFields([
        ...providerIdentifiers,
        ['Trengo ticket', conversation && conversation.ticket_id]
      ])
    },
    hasTemplateVariables(delivery) {
      const variables = delivery.template && delivery.template.variables
      return Boolean(variables && Object.keys(variables).length)
    },
    templateVariableFields(delivery) {
      return Object.entries(delivery.template.variables).map(([key, value]) => {
        let label = key.replace(/_/g, ' ').replace(/^./, letter => letter.toUpperCase())
        if (/^\d+$/.test(key)) label = `Variable ${key}`
        return [label, value]
      })
    },
    openVariable(variable) {
      this.selectedVariable = variable
    },
    compactIdentifier(value) {
      const text = String(value || '')
      if (text.length <= 11) return text
      return `${text.slice(0, 4)}…${text.slice(-4)}`
    },
    requestIdLabel(delivery) {
      if (delivery.attention && !delivery.attention.provider_attempted) return 'Internal request key'
      return 'Request ID'
    },
    async copyRequestId(value) {
      try {
        await navigator.clipboard.writeText(value)
        this.$notify({ type: 'success', title: 'Reference copied' })
      } catch (error) {
        this.$notify({ type: 'error', title: 'Unable to copy reference' })
      }
    }
  }
}
</script>
<style scoped lang="scss">
.automation-delivery {
  overflow: hidden;
  background: $ui-surface-soft;
  border-radius: $ui-overlay-radius;
}
.automation-delivery-body {
  padding: $ui-space-24;
}
.automation-delivery :deep(.automation-delivery-layout) {
  display: grid;
  gap: $ui-space-24;
  min-width: 0;
}
.automation-delivery-summary {
  padding: 0;
  background: transparent;
}
.automation-delivery-skipped {
  display: grid;
  gap: $ui-space-8;
  padding: $ui-space-16 $ui-space-20;
  border: 1px solid $ui-amber;
  border-radius: $ui-panel-radius;
  background: $ui-amber-soft;
}
.automation-delivery-skipped--failed {
  border-color: $ui-red;
  background: $ui-red-soft;
}
.automation-delivery-skipped__heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-12;
}
.automation-delivery-skipped p {
  margin: 0;
  color: $ui-text;
}
.automation-delivery-skipped small {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}
.automation-delivery-note {
  margin: 13px 0 0;
  padding-top: 9px;
  border-top: 1px solid #e8ebe5;
}
.automation-delivery-subject {
  display: block;
  margin-bottom: $ui-space-12;
  color: $ui-text;
  font-size: $ui-font-size-body;
}
.automation-delivery-message {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: $ui-text;
  font: inherit;
  line-height: 1.55;
}
.automation-delivery :deep(.automation-variables-card .automation-field-value--clamped) {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
.automation-reference-row {
  display: grid;
  grid-template-columns: minmax(130px, .85fr) minmax(0, 1.15fr);
  align-items: center;
  gap: 10px;
  margin-top: 9px;
  color: #7f8c8b;
  font-size: 15px;
  line-height: 20px;
}
.automation-reference-value {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: $ui-space-8;
  color: #344749;
  font-weight: 400;
  text-align: right;
}
.automation-copy-button {
  display: inline-flex;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: $ui-control-radius;
  background: $ui-blue;
  cursor: pointer;
}
.automation-copy-button:hover {
  filter: brightness(.92);
}
.automation-copy-button img {
  width: 16px;
  height: 16px;
}
.automation-attempt-row:focus-visible {
  outline: 2px solid $ui-blue;
  outline-offset: -2px;
}
.automation-attempt-row td:first-child {
  white-space: nowrap;
}
.automation-attempt-chevron {
  display: inline-flex;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  align-items: center;
  justify-content: center;
  margin-right: $ui-space-8;
  vertical-align: middle;
  transform: rotate(-90deg);
  transition: transform .2s ease;
}
.automation-attempt-chevron--open {
  transform: rotate(0);
}
.automation-attempt-chevron img {
  width: 10px;
  height: 6px;
}
.automation-attempt-detail-cell {
  padding: $ui-space-12 !important;
  background: $ui-surface-soft;
  white-space: normal !important;
}
.automation-delivery :deep(.automation-attempt-detail) {
  display: grid !important;
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  align-items: stretch;
  gap: $ui-space-12;
}
.automation-delivery :deep(.automation-attempt-detail .automation-detail-card) {
  min-width: 0;
}
.automation-attempt-json {
  margin: 0;
  padding: $ui-space-12;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background: $ui-surface-soft;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: $ui-text;
  font-size: $ui-font-size-metadata;
  line-height: 1.45;
}
@media (max-width: 760px) {
  .automation-delivery :deep(.automation-attempt-detail) {
    grid-template-columns: minmax(0, 1fr) !important;
  }
}
@media (max-width: 600px) {
  .automation-delivery-body {
    padding: $ui-space-12;
  }
  .automation-reference-row {
    grid-template-columns: minmax(0, 1fr);
    gap: $ui-space-4;
  }
  .automation-reference-value {
    justify-content: flex-start;
    text-align: left;
  }
}
</style>
